import { useAppDispatch, useAppSelector } from '@/Hook/hooks';
import { userAttendanceMark } from '@/Redux/Ducks/homeSlice';
import BaseMethods from 'AlignBaseUI/BaseMethods';
import { ControlTranslation, Modal } from 'AlignComponents';
import React, { useState } from 'react'
import { Button, ButtonGroup, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdOutlineShareLocation } from 'react-icons/md';
import { openAlert, openConfirmation } from '@/UI/BaseComponents/Alert';
import { RiFileList3Line } from 'react-icons/ri';

const Employee = () => {
  const dispatch = useAppDispatch();
  const employeeData = useAppSelector(x => x.Home.SelfService.employeeData);

  const [isAttendanceLogModalOpen, toggleAttendanceLogModal] = useState<boolean>(false);

  const currentDateTime = new Date;
  const currentDate = new Date(currentDateTime.getFullYear(), currentDateTime.getMonth(), currentDateTime.getDate());

  const { joiningDate, todayAttendance, currentMonthAttendance, weekHolidays, currentMonthLeave, holidayData, geoLocationData, portalAttendanceLog } = useAppSelector((state) => {
    const attendanceData = state.Home.SelfService.attendanceData.map((x: any) => {
      const attendanceDateTime = new Date(x.date);
      const attendanceDate = new Date(attendanceDateTime.getFullYear(), attendanceDateTime.getMonth(), attendanceDateTime.getDate());
      const timeIn = x.timein ? new Date(x.timein) : new Date("1900-01-01T00:00:00");
      const timeOut = x.timeout ? new Date(x.timeout) : new Date("1900-01-01T00:00:00");

      return {
        attendancedate: attendanceDate,
        attendancetimein: timeIn,
        attendancetimeout: timeOut
      }
    });
    const leaveData = state.Home.SelfService.leaveData.map((x: any) => ({ ...x, leavetransactiondate: new Date(x.leavedate) }));

    const holidayData = state.Home.SelfService.holidayData.map(x => ({ ...x, date: new Date(x.date) }));

    return {
      joiningDate: new Date(state.User.user.joiningdate),
      todayAttendance: attendanceData.find((x: any) => x.attendancedate.getTime() == currentDate.getTime()),
      currentMonthAttendance: attendanceData.filter((x: any) => x.attendancedate.getFullYear() == currentDate.getFullYear() && x.attendancedate.getMonth() == currentDate.getMonth()),
      weekHolidays: state.Home.SelfService.weekHolidays,
      currentMonthLeave: leaveData.filter((x: any) => x.leavetransactiondate.getFullYear() == currentDate.getFullYear() && x.leavetransactiondate.getMonth() == currentDate.getMonth()),
      holidayData: holidayData.filter(x => x.date.getFullYear() == currentDate.getFullYear() && x.date.getMonth() == currentDate.getMonth()),
      geoLocationData: state.Home.SelfService.geoLocationData,
      portalAttendanceLog: state.Home.SelfService.portalAttendanceLog
    }

  });

  let presentDaysCount = currentMonthAttendance.filter(x => x.attendancedate.getDate() < currentDate.getDate()).length;

  let offDaysCount = 0;
  let absentDaysCount = -presentDaysCount; // this is for calculate accurate days of absent
  let noofdays = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  var newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

  for (let i = 1; i <= noofdays; i++) {
    newDate.setDate(i);

    if (joiningDate.getTime() <= newDate.getTime()) {
      if (weekHolidays.indexOf(newDate.getDay()) != -1 || holidayData.findIndex(x => x.date.getTime() == newDate.getTime()) != -1) {
        offDaysCount++;
      }
      else if (newDate.getTime() < currentDate.getTime()) {
        absentDaysCount++;
      }
    }
  }

  // Today's Check-In and Check-Out Times
  const checkInTime: any = todayAttendance?.attendancetimein.getTime() !== new Date("1900-01-01T00:00:00").getTime()
    ? todayAttendance?.attendancetimein
    : null;

  const checkOutTime: any = todayAttendance?.attendancetimeout.getTime() !== new Date("1900-01-01T00:00:00").getTime()
    ? todayAttendance?.attendancetimeout
    : null;

  // Format Time as HH:MM:SS
  // Format Time as HH:MM:SS (24-hour format)
  const formatTime = (time: any) =>
    time ? time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : "00:00:00";

  // Calculate Today’s Hours in HH:MM
  const todayHours = checkInTime
    ? new Date((checkOutTime ? checkOutTime : currentDateTime.getTime()) - checkInTime).toISOString().substring(11, 16) // Extract HH:MM from ISO string
    : "00:00";

  const checkInPress = async () => {
    if (checkInTime) {
      const res = await openConfirmation({ title: "Do you want to check in again?", text: "you've already mark check in for today" });

      if (res.cancel)
        return;
    }

    if (!navigator.geolocation) {
      openAlert({ title: "Geolocation is not supported by your browser", text: "", icon: 'info' });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;

        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${userLat}&lon=${userLng}&accept-language=en`);
        const data = await response.json();

        const isWithinRadius = geoLocationData.some(({
          geofencinglatitude,
          geofencinglongitude,
          radiusmeters,
        }) => {
          const distance = calculateDistance(userLat, userLng, geofencinglatitude, geofencinglongitude);
          return distance <= radiusmeters;
        });

        if (geoLocationData.length >= 1 && !isWithinRadius) {
          openAlert({ title: "You are not within the allowed check-in/check-out location.", text: "", icon: 'error' });
          return;
        }

        dispatch(userAttendanceMark({
          type: "timein",
          latitude: userLat,
          longitude: userLng,
          address: data.display_name
        }));
      },
      (error) => {
        openAlert({ title: "Error getting location:", text: error.message, icon: 'error' });
      },
      { enableHighAccuracy: true }
    );
  }

  const checkOutPress = () => {
    if (!navigator.geolocation) {
      openAlert({ title: "Geolocation is not supported by your browser", text: "", icon: 'info' });

      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;

        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${userLat}&lon=${userLng}&accept-language=en`);
        const data = await response.json();

        const isWithinRadius = geoLocationData.some(({
          geofencinglatitude,
          geofencinglongitude,
          radiusmeters,
        }) => {
          const distance = calculateDistance(userLat, userLng, geofencinglatitude, geofencinglongitude);
          return distance <= radiusmeters;
        });

        if (geoLocationData.length >= 1 && !isWithinRadius) {
          openAlert({ title: "You are not within the allowed check-in/check-out location.", text: "", icon: 'error' });
          return;
        }

        dispatch(userAttendanceMark({
          type: "timeout",
          latitude: userLat,
          longitude: userLng,
          address: data.display_name
        }));
      },
      (error) => {
        openAlert({ title: "Error getting location:", text: error.message, icon: 'error' });
      },
      { enableHighAccuracy: true }
    );
  }

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const R = 6371000; // Earth's radius in meters
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in meters
  };

  const openModal = () => {
    toggleAttendanceLogModal(true);
  }

  const closeModal = () => {
    toggleAttendanceLogModal(false);
  }

  return (
    <>
      <Modal
        close={closeModal}
        heading={<ControlTranslation system={true} controlId="lblattendancelogs" />}
        show={isAttendanceLogModalOpen}
        modalSize="sm"
      >
        <div className="check-logs">
          {
            portalAttendanceLog.map(x => (
              <div className={`check-${x.attendanceactivityno == 0 ? 'in' : 'out'}`}>
                <ControlTranslation system={true} controlId={`lblcheck${x.attendanceactivityno == 0 ? 'in' : 'out'}`} />:
                <span>{formatTime(new Date(x.attendanceactivitydatetime))}</span>
              </div>
            ))
          }
        </div>
      </Modal>
      <Card className="employee-card">
        <Card.Body>
          <div className="sections">
            <div className="user-info">
              <div className="image-section">
                <img src={BaseMethods.getUserPhoto(employeeData.employeeimage)} alt="" />
                <div className="check-group">
                  {
                    checkInTime && todayHours ? (
                      <div className="today-hours">
                        <span>
                          <ControlTranslation system={true} controlId="lbltodayhours" />
                        </span>

                        {todayHours}
                      </div>
                    ) : null
                  }

                  {/* {checkInTime ? (
                  checkOutTime ? (
                    <span className="day-complete">
                      <ControlTranslation system={true} controlId="lbldaycomplete" />
                    </span>
                  ) : (
                    <Button variant="primary" onClick={checkOutPress}>
                      <ControlTranslation system={true} controlId="lblcheckout" />
                      {
                        geoLocationData.length >= 1 ? (
                          <MdOutlineShareLocation size={18} className="ms-2" />
                        ) : null
                      }
                    </Button>
                  )
                ) : (
                  <Button variant="primary" onClick={checkInPress}>
                    <ControlTranslation system={true} controlId="lblcheckin" />
                    {
                      geoLocationData.length >= 1 ? (
                        <MdOutlineShareLocation size={18} className="ms-2" />
                      ) : null
                    }
                  </Button>
                )} */}

                  <div className="d-flex align-items-center">
                    <ButtonGroup>
                      <Button variant="outline-primary" onClick={checkInPress} style={{ minWidth: "120px" }}>
                        <ControlTranslation system={true} controlId="lblcheckin" />
                        {
                          geoLocationData.length >= 1 ? (
                            <MdOutlineShareLocation size={18} className="ms-2" />
                          ) : null
                        }
                      </Button>
                      <Button variant="outline-primary" onClick={checkOutPress} style={{ minWidth: "120px" }}>
                        <ControlTranslation system={true} controlId="lblcheckout" />
                        {
                          geoLocationData.length >= 1 ? (
                            <MdOutlineShareLocation size={18} className="ms-2" />
                          ) : null
                        }
                      </Button>
                    </ButtonGroup>
                    {
                      portalAttendanceLog.length >= 1 ? (
                        <OverlayTrigger placement="bottom" overlay={<Tooltip><ControlTranslation system={true} controlId="lblattendancelogs" /></Tooltip>}>
                          <Button variant="primary" className="ms-2" onClick={openModal}>
                            <RiFileList3Line size={18} />
                          </Button>
                        </OverlayTrigger>
                      ) : null
                    }
                  </div>

                </div>
              </div>
              <div className="info-section">
                <h6 className="employee">
                  {employeeData.employeestxt}
                </h6>
                <span className="position">
                  {employeeData.positionstxt}
                </span>

                <span className="employee-info">
                  <span>
                    <ControlTranslation system={true} controlId="lblfunctionalarea" />:
                  </span>
                  <span className="value">{employeeData.functionalareastxt}</span>
                </span>

                <span className="employee-info">
                  <span>
                    <ControlTranslation system={true} controlId="lbldepartment" />:
                  </span>
                  <span className="value">{employeeData.departmentstxt}</span>
                </span>

                <span className="employee-info">
                  <span>
                    <ControlTranslation system={true} controlId="lblemail" />:
                  </span>
                  <span className="value">{employeeData.email}</span>
                </span>

                <div className="check-info">
                  {
                    checkInTime ? (
                      <div className="check-in">
                        <ControlTranslation system={true} controlId="lblcheckin" />:
                        <span>{formatTime(checkInTime)}</span>
                      </div>
                    ) : null
                  }
                  {
                    checkOutTime ? (
                      <div className="check-out">
                        <ControlTranslation system={true} controlId="lblcheckout" />:
                        <span>{formatTime(checkOutTime)}</span>
                      </div>
                    ) : null
                  }
                </div>
              </div>
            </div>
            <div className="attendance-info-cards mt-auto">
              <div className="attendance-info-card">
                <span>
                  <ControlTranslation system={true} controlId="lblpresent" />
                </span>
                <h6>{presentDaysCount}</h6>
              </div>
              <div className="attendance-info-card absent">
                <span>
                  <ControlTranslation system={true} controlId="lblabsent" />
                </span>
                <h6>{absentDaysCount}</h6>
              </div>
              <div className="attendance-info-card leaves">
                <span>
                  <ControlTranslation system={true} controlId="lblleave" />
                </span>
                <h6>{currentMonthLeave.length}</h6>
              </div>
              <div className="attendance-info-card offDays">
                <span>
                  <ControlTranslation system={true} controlId="lbloffdays" />
                </span>
                <h6>{offDaysCount}</h6>
              </div>
              <div className="attendance-info-card missing">
                <span>
                  <ControlTranslation system={true} controlId="lblmissing" />
                </span>
                <h6>-</h6>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </>
  )
}

export default Employee